import React, { useEffect, useMemo, useState } from "react";
import StatCard from "./components/StatCard";
import TimeSeriesChart from "./components/TimeSeriesChart";
import TopKTable from "./components/TopKTable";
import { promQuery, promQueryRange, rangeWindowMs, toSeries } from "./prom";
import { getTenantUuid } from "@/app/ingress/tenant";

function hasAnyPoints(series: any[]): boolean {
  if (!Array.isArray(series)) return false;
  return series.some((s) => Array.isArray(s?.points) && s.points.length > 0);
}

export default function AdminObservabilityProviderHealthPage() {
  const tenantUuid = getTenantUuid();

  const [topProviders, setTopProviders] = useState<Array<Record<string, any>>>([]);
  const [rateLimitProviders, setRateLimitProviders] = useState<Array<Record<string, any>>>([]);
  const [providerErrSeries, setProviderErrSeries] = useState<any[]>([]);
  const [providerLatencySeries, setProviderLatencySeries] = useState<any[]>([]);
  const [latencyMetricAvailable, setLatencyMetricAvailable] = useState<boolean>(false);

  // UX: if no error series exists, show callout + hide the chart
  const [providerErrHasData, setProviderErrHasData] = useState<boolean>(false);
  const [providerErrEmptyMsg, setProviderErrEmptyMsg] = useState<string>("");

  useEffect(() => {
    let alive = true;

    async function run() {
      // 1) Top calls by provider/model (15m)
      try {
        const q = `topk(10, sum by (provider,model) (increase(llm_calls_total_by_tenant_total{tenant_id="${tenantUuid}"}[15m])))`;
        //const q = `topk(10, sum by (provider,model) (increase(llm_calls_total[15m])))`;
        const resp = await promQuery(q, tenantUuid);
        const ss = toSeries(resp);
        const rows = ss.map((s) => ({
          provider: s.metric.provider || "unknown",
          model: s.metric.model || "unknown",
          calls_15m: s.points[0]?.v ?? 0,
        }));
        if (!alive) return;
        setTopProviders(rows.sort((a, b) => (b.calls_15m ?? 0) - (a.calls_15m ?? 0)));
      } catch {
        if (!alive) return;
        setTopProviders([]);
      }

      // 2) Rate limits by provider/model (15m)
      try {
        const q = `topk(10, sum by (provider,model) (increase(llm_rate_limit_total[15m])))`;
        const resp = await promQuery(q, tenantUuid);
        const ss = toSeries(resp);
        const rows = ss.map((s) => ({
          provider: s.metric.provider || "unknown",
          model: s.metric.model || "unknown",
          rl_15m: s.points[0]?.v ?? 0,
        }));
        if (!alive) return;
        setRateLimitProviders(rows.sort((a, b) => (b.rl_15m ?? 0) - (a.rl_15m ?? 0)));
      } catch {
        if (!alive) return;
        setRateLimitProviders([]);
      }

      // 3) Error rate time series (rolling 5m)
      // UX improvement: if series is empty, show a “No errors observed” callout.
      try {
        const { start, end } = rangeWindowMs(2);
        const q = `
        (
          sum by (provider,model) (rate(llm_calls_total_by_tenant_total{tenant_id="${tenantUuid}",model!="unknown",result="error"}[5m]))
        )
        /
        clamp_min(sum by (provider,model) (rate(llm_calls_total_by_tenant_total{tenant_id="${tenantUuid}",model!="unknown"}[5m])), 0.000001)
                `.trim();

        const resp = await promQueryRange(q, tenantUuid, { start, end, step: "30s" });
        const ss = toSeries(resp);
        const series = ss.map((s) => ({
          name: `${s.metric.provider || "unknown"} · ${s.metric.model || "unknown"}`,
          points: s.points,
        }));

        if (!alive) return;

        const any = hasAnyPoints(series);
        setProviderErrSeries(any ? series : []);
        setProviderErrHasData(any);
        setProviderErrEmptyMsg(any ? "" : `No errors observed in last 2h.`);
      } catch {
        if (!alive) return;
        setProviderErrSeries([]);
        setProviderErrHasData(false);
        setProviderErrEmptyMsg("No error-rate data available (query failed or metric not emitted).");
      }

      // 4) Latency (optional metric)
      // We only query if llm_call_latency_seconds_bucket exists to avoid empty/noisy panels.
      try {
        const existsQ = `count(llm_call_latency_seconds_by_tenant_bucket{tenant_id="${tenantUuid}"})`;
        //const existsQ = `count(llm_call_latency_seconds_bucket)`;
        const existsResp = await promQuery(existsQ, tenantUuid);
        const existsSeries = toSeries(existsResp);
        const existsVal = existsSeries?.[0]?.points?.[0]?.v ?? 0;
        const available = Number(existsVal) > 0;
        if (!alive) return;
        setLatencyMetricAvailable(available);

        if (!available) {
          setProviderLatencySeries([]);
          return;
        }

        const { start, end } = rangeWindowMs(2);
        const q = `histogram_quantile(0.95, sum by (le,provider) (rate(llm_call_latency_seconds_by_tenant_bucket{tenant_id="${tenantUuid}"}[5m])))`;
        //const q = `histogram_quantile(0.95, sum by (le,provider) (rate(llm_call_latency_seconds_bucket[5m])))`;
        const resp = await promQueryRange(q, tenantUuid, { start, end, step: "30s" });
        const ss = toSeries(resp);
        const series = ss.map((s) => ({ name: s.metric.provider || "unknown", points: s.points }));
        if (!alive) return;
        setProviderLatencySeries(series);
      } catch {
        if (!alive) return;
        setLatencyMetricAvailable(false);
        setProviderLatencySeries([]);
      }
    }

    run();
    const t = window.setInterval(run, 20_000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [tenantUuid]);

  const topCall = topProviders[0];
  const topRl = rateLimitProviders[0];

  return (
    <div className="p-6 space-y-6">
      <div className="text-xl font-semibold">Observability · Provider Health</div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          title="Top provider calls (15m)"
          value={topCall ? `${topCall.provider}` : "—"}
          sub={topCall ? `${(topCall.calls_15m ?? 0).toFixed(0)} calls` : undefined}
        />
        <StatCard
          title="Top rate-limits (15m)"
          value={topRl ? `${topRl.provider}` : "—"}
          sub={topRl ? `${(topRl.rl_15m ?? 0).toFixed(0)} events` : undefined}
        />
        <StatCard
          title="LLM latency p95"
          value={latencyMetricAvailable ? "Available" : "Not emitting"}
          sub={
            latencyMetricAvailable
              ? "Derived from llm_call_latency_seconds_by_tenant_bucket."
              : "Enable llm_call_latency_seconds histogram to power this panel."
          }
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <TopKTable
          title="Top providers/models (calls, last 15m)"
          columns={[
            { key: "provider", label: "provider", mono: true },
            { key: "model", label: "model", mono: true },
            { key: "calls_15m", label: "calls_15m" },
          ]}
          rows={topProviders.map((r) => ({ ...r, calls_15m: (r.calls_15m ?? 0).toFixed(0) }))}
        />

        <TopKTable
          title="Rate limit events (last 15m)"
          columns={[
            { key: "provider", label: "provider", mono: true },
            { key: "model", label: "model", mono: true },
            { key: "rl_15m", label: "rate_limits_15m" },
          ]}
          rows={rateLimitProviders.map((r) => ({ ...r, rl_15m: (r.rl_15m ?? 0).toFixed(0) }))}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="space-y-2">
          {!providerErrHasData ? (
            <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
              {providerErrEmptyMsg || "No errors observed in last 2h."}
            </div>
          ) : (
            <TimeSeriesChart title="Provider error rate (rolling 5m)" series={providerErrSeries} height={320} />
          )}
        </div>

        <TimeSeriesChart title="Provider latency p95 (rolling 5m)" series={providerLatencySeries} height={320} />
      </div>
    </div>
  );
}